<template>
  <div>
    <div class="navbar">
      <button class="btn btn-back" @click="goBack">Back</button>
      <h2 class="title">{{ mode === 'single' ? 'Add Single Image' : 'Bulk Upload' }}</h2>
      <div class="mode-toggle">
        <button :class="['btn-tab', mode === 'single' ? 'active' : '']" @click="mode = 'single'">Single</button>
        <button :class="['btn-tab', mode === 'bulk' ? 'active' : '']" @click="mode = 'bulk'">Bulk</button>
      </div>
    </div>

    <div class="warning-banner">
      <i class="fas fa-exclamation-triangle"></i>
      <span>Please ensure you only save/use <strong>Public Domain</strong> content or <strong>UNB's own content</strong>.</span>
    </div>

    <div v-if="mode === 'single'" class="form-container">
      
      <div class="form-group">
        <label for="name">Name:</label>
        <input id="name" v-model="character.name" placeholder="Enter image name" />
      </div>

      <div class="form-group">
        <label for="title">ALT TEXT (Title):</label>
        <input
          id="title"
          v-model="character.title"
          placeholder="Enter description for screen readers"
          @input="generateHtmlCode"
        />
      </div>
      
      <div class="form-group">
        <label for="path">Path / URL / Embed Code:</label>
        <input
          id="path"
          v-model="character.path"
          placeholder="Paste URL or <iframe> code here..."
          @input="onPathChange"
        />
        <span class="hint" v-if="wasEmbedExtracted">✓ Extracted URL from embed code automatically</span>
      </div>
      
      <div class="form-row">
        <div class="form-group half-width">
          <label for="source">Source (Mandatory):</label>
          <input
            id="source"
            v-model="character.source"
            placeholder="e.g. UNB Library"
            :class="{ 'error-border': attemptSubmit && !character.source }"
          />
          <span v-if="attemptSubmit && !character.source" class="error-text">Source is required</span>
        </div>

        <div class="form-group half-width">
          <label for="addedBy">Added By (Optional):</label>
          <input id="addedBy" v-model="character.addedBy" placeholder="Your name" />
        </div>
      </div>

      <div class="form-group">
        <label>Details (Optional):</label>
        <textarea 
          v-model="character.details" 
          rows="3" 
          placeholder="Add details about the image (e.g. prompts used if this is an AI-generated image)..."
        ></textarea>
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="character.isAi" />
          <span class="label-text"><i class="fas fa-robot"></i> AI Generated Image</span>
        </label>
      </div>

      <div class="form-group">
        <label>Art Style (Optional):</label>
        <input v-model="character.artStyle" placeholder="e.g. Pixel Art, Photorealistic, Sketch..." />
      </div>

      <div v-if="character.path" class="form-group">
        <label>Preview:</label>
        <div class="media-preview-container">
          <img v-if="mediaType === 'image'" :src="resolveUrl(character.path)" alt="Preview" @error="handleImageError" @load="onImageLoad" ref="previewImage" class="preview-content" />
          <video v-else-if="mediaType === 'video'" controls :src="resolveUrl(character.path)" class="preview-content"></video>
          <audio v-else-if="mediaType === 'audio'" controls :src="resolveUrl(character.path)" class="preview-audio"></audio>
          <iframe v-else-if="mediaType === 'embed'" :src="embedUrl" class="preview-content" frameborder="0" allowfullscreen></iframe>
        </div>
      </div>

      <div v-if="mediaType === 'image'" class="form-group">
        <label>Image Size (Optional)</label>
        <div class="size-controls">
          <div class="size-input-group">
            <label class="small-label">Width:</label>
            <input type="number" v-model.number="imageSize.width" placeholder="Auto" min="1" @input="onWidthChange" />
            <span class="unit">px</span>
          </div>
          <button type="button" class="btn-lock" @click="toggleLock"><i :class="isLocked ? 'fas fa-lock' : 'fas fa-lock-open'"></i></button>
          <div class="size-input-group">
            <label class="small-label">Height:</label>
            <input type="number" v-model.number="imageSize.height" placeholder="Auto" min="1" @input="onHeightChange" />
            <span class="unit">px</span>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>HTML / Embed Code:</label>
        <textarea v-model="character.htmlCode" readonly></textarea>
      </div>

      <div class="form-group">
        <label>Tags:</label>
        <span class="field-instruction">Separate tags with commas. Use the list below to find existing tags.</span>
        
        <input 
          v-model="tagsInput" 
          placeholder="Start typing to see suggestions..." 
          list="all-tags" 
        />
        <datalist id="all-tags">
          <option v-for="tag in allUniqueTags" :key="tag" :value="tag" />
        </datalist>

        <div v-if="commonTags.length > 0" class="tag-suggestions">
          <span class="suggestion-label">Popular tags:</span>
          <div class="suggestion-list">
            <button v-for="tag in commonTags" :key="tag" class="tag-btn" @click="addTag(tag)" type="button">+ {{ tag }}</button>
          </div>
        </div>
      </div>

      <div class="action-buttons">
        <button class="btn btn-save" @click="addCharacter">Add Image</button>
      </div>
    </div>

    <div v-else class="form-container">
      <div class="info-box">
        <i class="fas fa-info-circle"></i>
        <p>Drag & Drop files to grab names. Base Path + Filename will be used.</p>
      </div>

      <div class="form-group">
        <label>Base Path (Folder URL):</label>
        <input v-model="bulk.basePath" placeholder="e.g. https://lms.unb.ca/images/" />
      </div>

      <div class="form-row">
        <div class="form-group half-width">
          <label>Source (Mandatory):</label>
          <input v-model="bulk.source" />
        </div>
        <div class="form-group half-width">
          <label>Added By:</label>
          <input v-model="bulk.addedBy" />
        </div>
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="bulk.isAi" />
          <span class="label-text"><i class="fas fa-robot"></i> AI Generated (Applies to all)</span>
        </label>
      </div>

      <div class="form-group">
        <label>Art Style (Common):</label>
        <input v-model="bulk.artStyle" />
      </div>

      <div class="form-group">
        <label>Common Tags:</label>
        <input v-model="bulk.tagsInput" list="all-tags" placeholder="Tags applied to ALL images" />
      </div>

      <div class="drop-zone" @dragover.prevent @drop.prevent="handleDrop">
        <div class="drop-content">
          <i class="fas fa-cloud-upload-alt fa-3x"></i>
          <p>Drag files here</p>
          <p class="or">- OR -</p>
          <textarea v-model="bulk.rawText" placeholder="Paste filenames..." @input="processBulkText"></textarea>
        </div>
      </div>

      <div v-if="bulkList.length > 0" class="bulk-preview">
        <h3>Preview ({{ bulkList.length }} items)</h3>
        <div class="preview-list">
          <div v-for="(item, idx) in bulkList" :key="idx" class="preview-item">
            <span class="preview-name">{{ item.name }}</span>
            <span class="preview-path">{{ item.path }}</span>
            <button class="btn-remove" @click="removeBulkItem(idx)"><i class="fas fa-times"></i></button>
          </div>
        </div>
        <button class="btn btn-save full-width" @click="submitBulk" :disabled="loading">
          {{ loading ? 'Uploading...' : 'Upload All' }}
        </button>
      </div>
    </div>

    <AppOverlay :visible="added" message="Success!" />
  </div>
</template>

<script>
import api from '../services/api';
import AppOverlay from './AppOverlay.vue';

export default {
  components: { AppOverlay },
  data() {
    return {
      mode: 'single',
      loading: false, added: false, attemptSubmit: false, wasEmbedExtracted: false,
      
      // Single Data
      character: { name: '', title: '', path: '', htmlCode: '', tags: [], styles: '', preferredWidth: null, preferredHeight: null, addedBy: '', source: '', details: '', isAi: false, artStyle: '' },
      imageSize: { width: null, height: null },
      originalSize: { width: null, height: null },
      isLocked: true, aspectRatio: null, tagsInput: '',
      
      // Bulk Data
      bulk: { basePath: '', source: '', addedBy: '', tagsInput: '', rawText: '', isAi: false, artStyle: '' },
      bulkList: [],
      existingCharacters: [],
    };
  },
  computed: {
    allUniqueTags() {
      const tags = new Set();
      this.existingCharacters.forEach(c => {
        if (c.tags && Array.isArray(c.tags)) c.tags.forEach(t => tags.add(t));
      });
      return Array.from(tags).sort();
    },
    commonTags() {
      const counts = {};
      this.existingCharacters.forEach(c => {
        if (c.tags && Array.isArray(c.tags)) c.tags.forEach(t => { counts[t] = (counts[t] || 0) + 1; });
      });
      return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 12).map(e => e[0]);
    },
    mediaType() {
      const path = this.character.path ? this.character.path.toLowerCase().split('?')[0] : '';
      if (path.includes('youtube.com') || path.includes('youtu.be') || path.includes('vimeo.com')) return 'embed';
      if (path.endsWith('.mp4') || path.endsWith('.webm') || path.endsWith('.ogg') || path.endsWith('.mov')) return 'video';
      if (path.endsWith('.mp3') || path.endsWith('.wav')) return 'audio';
      return 'image';
    },
    embedUrl() {
      let url = this.character.path;
      if (url.includes('youtube.com/watch?v=')) return url.replace('watch?v=', 'embed/');
      if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'www.youtube.com/embed/');
      return url;
    }
  },
  methods: {
    resolveUrl(path) {
      if (!path) return '';
      if (path.startsWith('/shared/online/')) return 'https://lms.unb.ca' + path;
      return path;
    },
    onPathChange() {
      this.wasEmbedExtracted = false;
      const raw = this.character.path;
      const srcMatch = raw.match(/src\s*=\s*["']([^"']+)["']/i);
      if (srcMatch && srcMatch[1]) {
        this.character.path = srcMatch[1];
        this.wasEmbedExtracted = true;
      }
      this.imageSize.width = null; this.imageSize.height = null;
      this.generateHtmlCode();
    },
    generateHtmlCode() {
      const type = this.mediaType;
      const fullPath = this.resolveUrl(this.character.path);
      const title = this.character.title || this.character.name || 'Image';
      if (!fullPath) { this.character.htmlCode = ''; return; }

      if (type === 'video') this.character.htmlCode = `<p><video src="${fullPath}" controls style="max-width: 100%;"></video></p>`;
      else if (type === 'audio') this.character.htmlCode = `<p><audio src="${fullPath}" controls></audio></p>`;
      else if (type === 'embed') this.character.htmlCode = `<p><iframe src="${this.embedUrl}" width="560" height="315" frameborder="0" allowfullscreen></iframe></p>`;
      else {
        let styleAttr = 'max-width: 100%;';
        if (this.imageSize.width || this.imageSize.height) {
          const w = this.imageSize.width ? `width: ${this.imageSize.width}px;` : '';
          const h = this.imageSize.height ? `height: ${this.imageSize.height}px;` : '';
          styleAttr = `${w} ${h} max-width: 100%;`.trim();
        }
        this.character.htmlCode = `<p><img src="${fullPath}" alt="${title}" title="${title}" data-d2l-editor-default-img-style="true" style="${styleAttr}"></p>`;
      }
    },
    async fetchExistingTags() { try { this.existingCharacters = await api.getAllCharacters(); } catch (e) { console.error(e); } },
    addTag(tag) { const current = this.tagsInput.split(',').map(t => t.trim()).filter(t => t); if (!current.includes(tag)) { current.push(tag); this.tagsInput = current.join(', '); } },
    addBulkTag(tag) { const current = this.bulk.tagsInput.split(',').map(t => t.trim()).filter(t => t); if (!current.includes(tag)) { current.push(tag); this.bulk.tagsInput = current.join(', '); } },
    onImageLoad(event) { this.aspectRatio = event.target.naturalWidth / event.target.naturalHeight; },
    toggleLock() { this.isLocked = !this.isLocked; },
    onWidthChange() { if (this.isLocked && this.aspectRatio) this.imageSize.height = Math.round(this.imageSize.width / this.aspectRatio); this.generateHtmlCode(); },
    onHeightChange() { if (this.isLocked && this.aspectRatio) this.imageSize.width = Math.round(this.imageSize.height * this.aspectRatio); this.generateHtmlCode(); },
    handleImageError(event) { event.target.src = 'https://via.placeholder.com/300x300?text=Invalid+URL'; },
    
    async addCharacter() {
      this.attemptSubmit = true;
      if (!this.character.name || !this.character.path || !this.character.source) { alert('Name, Path, and Source are required.'); return; }
      this.generateHtmlCode();
      this.character.tags = this.tagsInput.split(',').map(t => t.trim()).filter(t => t);
      this.character.preferredWidth = this.imageSize.width;
      this.character.preferredHeight = this.imageSize.height;

      try {
        await api.createCharacter(this.character);
        this.added = true;
        this.character = { name: '', title: '', path: '', htmlCode: '', styles: '', preferredWidth: null, preferredHeight: null, addedBy: '', source: '', details: '', isAi: false, artStyle: '', tags: [] };
        this.imageSize = { width: null, height: null };
        this.tagsInput = '';
        this.attemptSubmit = false;
        setTimeout(() => { this.added = false; }, 2000);
      } catch (error) { console.error(error); alert('Failed to add image.'); }
    },

    handleDrop(e) { const files = Array.from(e.dataTransfer.files); const names = files.map(f => f.name); if (this.bulk.rawText) this.bulk.rawText += '\n'; this.bulk.rawText += names.join('\n'); this.processBulkText(); },
    processBulkText() {
      const lines = this.bulk.rawText.split(/\n/).map(l => l.trim()).filter(l => l);
      this.bulkList = lines.map(filename => {
        const name = filename.replace(/\.[^/.]+$/, "").replace(/[_-]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
        let path = (this.bulk.basePath || '') + filename;
        let fullPath = path.startsWith('/shared/online/') ? 'https://lms.unb.ca' + path : path;
        return { name: name, path: path, title: name, source: this.bulk.source, addedBy: this.bulk.addedBy, htmlCode: `<p><img src="${fullPath}" alt="${name}" style="max-width: 100%;"></p>` };
      });
    },
    removeBulkItem(index) { this.bulkList.splice(index, 1); },
    async submitBulk() {
      if (!this.bulk.source || !this.bulk.basePath) { alert("Base Path and Source are mandatory."); return; }
      if (this.bulkList.length === 0) { alert("No files to upload."); return; }
      this.loading = true;
      const tags = this.bulk.tagsInput.split(',').map(t => t.trim()).filter(t => t);
      const payload = this.bulkList.map(item => ({ 
        ...item, tags: tags, source: this.bulk.source, addedBy: this.bulk.addedBy, isAi: this.bulk.isAi, artStyle: this.bulk.artStyle 
      }));
      try {
        await api.createBatchCharacters(payload);
        this.added = true;
        this.bulkList = []; this.bulk.rawText = '';
        setTimeout(() => { this.added = false; }, 2000);
      } catch (e) { alert("Batch upload failed: " + e.message); } finally { this.loading = false; }
    },
    goBack() { this.$router.push({ name: 'CharacterList' }); },
  },
  
  // CHECK FOR CLONE DATA (using sessionStorage for reliability)
  created() {
    const cloneData = sessionStorage.getItem('cloneData');
    if (cloneData) {
      try {
        const data = JSON.parse(cloneData);
        // We purposefully don't copy Name or Path so they are unique
        this.character.source = data.source || '';
        // Map database snake_case to frontend camelCase
        this.character.addedBy = data.added_by || data.addedBy || ''; 
        this.character.details = data.details || '';
        this.character.artStyle = data.art_style || data.artStyle || '';
        this.character.isAi = !!data.is_ai || !!data.isAi;
        
        if (Array.isArray(data.tags)) {
          this.tagsInput = data.tags.join(', ');
        }
        // Clean up
        sessionStorage.removeItem('cloneData');
      } catch (e) {
        console.error("Failed to parse clone data", e);
      }
    }
  },
  mounted() { this.fetchExistingTags(); }
};
</script>

<style scoped>
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

.navbar { display: flex; justify-content: space-between; align-items: center; background-color: var(--primary-color); padding: 15px 20px; color: white; }
.title { font-size: 24px; font-weight: bold; }
.btn-back { background-color: var(--danger-color); color: white; padding: 8px 16px; border-radius: 4px; }
.mode-toggle { display: flex; background: rgba(255,255,255,0.2); border-radius: 4px; padding: 4px; gap: 5px; }
.btn-tab { border: none; background: transparent; padding: 6px 12px; cursor: pointer; border-radius: 4px; font-weight: bold; color: white; }
.btn-tab.active { background: white; color: var(--primary-color); box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
.form-container { max-width: 800px; margin: 40px auto; padding: 20px; background-color: #ffffff; border-radius: 8px; border: 2px solid #000; border-bottom: 4px solid #000; box-shadow: 0 12px 24px rgba(0,0,0,0.2); }
.warning-banner { max-width: 800px; margin: 20px auto 0; padding: 15px; background: #fff3cd; color: #856404; border: 1px solid #ffeeba; border-radius: 4px; display: flex; align-items: center; gap: 10px; }
.form-group { display: flex; flex-direction: column; margin-bottom: 20px; }
.form-group label { margin-bottom: 8px; font-weight: bold; }
.field-instruction { font-size: 12px; color: #666; margin-bottom: 5px; }
.form-group input, .form-group textarea { padding: 10px; border: 1px solid #000; border-radius: 4px; font-size: 16px; background-color: rgba(246,219,53); }
.form-group textarea[readonly] { background-color: rgba(246,219,53, 0.5); cursor: not-allowed; }
.hint { font-size: 13px; color: #27ae60; font-weight: bold; margin-top: 5px; }
.form-row { display: flex; gap: 20px; }
.half-width { flex: 1; }
.error-border { border: 2px solid var(--danger-color) !important; }
.error-text { color: var(--danger-color); font-size: 12px; margin-top: 4px; }
.media-preview-container { width: 100%; border: 2px solid #000; border-radius: 8px; overflow: hidden; background-color: #f5f5f5; display: flex; justify-content: center; align-items: center; min-height: 200px; padding: 20px; }
.preview-content { max-width: 100%; max-height: 300px; display: block; border-radius: 4px; }
.preview-audio { width: 100%; }
.size-controls { display: flex; gap: 15px; align-items: center; }
.size-input-group { display: flex; align-items: center; gap: 8px; }
.size-input-group input { width: 80px; }
.btn-lock { background: transparent; border: 2px solid #000; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; cursor: pointer; }
.action-buttons { display: flex; justify-content: flex-end; }
.btn-save { background-color: var(--accent-color); color: white; padding: 10px 20px; border-radius: 4px; font-weight: bold; }
.tag-suggestions { margin-top: 10px; padding: 10px; background: #f9f9f9; border-radius: 4px; border: 1px solid #eee; }
.suggestion-label { display: block; font-size: 12px; color: #666; margin-bottom: 5px; font-weight: bold; }
.suggestion-list { display: flex; flex-wrap: wrap; gap: 5px; }
.tag-btn { background: #e0f7fa; border: 1px solid #b2ebf2; padding: 4px 8px; border-radius: 12px; font-size: 12px; cursor: pointer; color: #006064; }
.tag-btn:hover { background: #b2ebf2; }
.info-box { background: #e3f2fd; padding: 15px; border-radius: 4px; display: flex; gap: 10px; align-items: center; margin-bottom: 20px; color: #0d47a1; border-left: 5px solid #0d47a1; }
.drop-zone { border: 3px dashed #ccc; border-radius: 8px; padding: 40px; text-align: center; margin-bottom: 20px; background: #fafafa; }
.drop-content textarea { width: 100%; height: 150px; margin-top: 15px; border: 1px solid #ddd; padding: 10px; background: white; }
.or { margin: 10px 0; font-weight: bold; color: #999; }
.preview-list { max-height: 300px; overflow-y: auto; border: 1px solid #eee; margin-bottom: 20px; border-radius: 4px; }
.preview-item { display: flex; justify-content: space-between; padding: 10px; border-bottom: 1px solid #eee; align-items: center; background: #fff; }
.btn-remove { border: none; background: transparent; color: red; cursor: pointer; font-size: 16px; }
.full-width { width: 100%; }
.checkbox-group { margin-top: 10px; }
.checkbox-label { display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: bold; }
.label-text { display: flex; align-items: center; gap: 5px; }

@media (max-width: 768px) {
  .form-row { flex-direction: column; gap: 0; }
}
</style>